import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class IndexedDBService {
  private dbName: string = 'appDB';
  private dbVersion: number = 1;
  private db: IDBDatabase;

  public constructor() {
  }

  public init(): void {
    this.persistStorage();
    this.openDatabase().catch(err => console.error('IndexedDB pre-warm failed:', err));
  }

  /**
   * Saves data into the specified object store within the IndexedDB database.
   * @template T - The type of the data to be saved.
   * @param {string} storeName - The name of the object store where the data will be saved.
   * @param {T} data - The data to be saved in the object store.
   * @returns {Promise<void>} A promise that resolves when the data is successfully saved,
   * or rejects with an error if the operation fails.
   */
  public async saveData<T>(storeName: string, data: T): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = (): void => resolve();
      request.onerror = (event: Event): void => reject((event.target as IDBRequest).error);
    });
  }

  /**
   * Retrieves data from the specified object store in the IndexedDB by its ID.
   *
   * @template T - The type of the data to be retrieved.
   * @param storeName - The name of the object store to query.
   * @param id - The unique identifier of the data to retrieve.
   * @returns A promise that resolves with the retrieved data of type `T`, or `undefined` if no data is found.
   * @throws An error if the retrieval operation fails.
   */
  public getData<T>(storeName: string, id: string): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      this.openDatabase().then((db) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(id);
        console.debug(`Retrieving data from store: ${storeName} with ID: ${id}`);

        request.onsuccess = (event: Event): void => resolve((event.target as IDBRequest).result);
        request.onerror = (event: Event): void => reject((event.target as IDBRequest).error);
      }).catch((error) => {
        console.error('Error getting store:', error);
        reject(error);
      });
    });
  }

  /**
   * Deletes a record from the specified object store in the IndexedDB database.
   *
   * @param storeName - The name of the object store from which the record should be deleted.
   * @param id - The unique identifier of the record to delete.
   * @returns A promise that resolves when the record is successfully deleted, or rejects with an error if the operation fails.
   */
  public deleteData(storeName: string, id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.openDatabase().then((db) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete(id);

        request.onsuccess = (): void => resolve();
        request.onerror = (event: Event): void => reject((event.target as IDBRequest).error);
      }).catch((error) => {
        console.error('Error getting store:', error);
        reject(error);
      });
    });
  }

  /**
   * Retrieves all records from the specified object store in the IndexedDB database.
   *
   * @param storeName - The name of the object store to retrieve records from.
   * @returns A promise that resolves with an array of all records in the specified store,
   *          or rejects with an error if the operation fails.
   */
  public getStore(storeName: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      this.openDatabase().then((db) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();

        request.onsuccess = (event: Event): void => resolve((event.target as IDBRequest).result);
        request.onerror = (event: Event): void => reject((event.target as IDBRequest).error);
      }).catch((error) => {
        console.error('Error getting store:', error);
        reject(error);
      });
    });
  }

  public async deleteDataInStoreWhereKeyContains(storeName: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.openDatabase().then((db) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.openCursor(IDBKeyRange.bound(key, key + '\uffff'));

        request.onsuccess = (event: Event): void => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = (event: Event): void => {
          reject((event.target as IDBRequest).error);
        };
      }).catch((error) => {
        console.error('Error deleting data in store:', error);
        reject(error);
      });
    });
  }

  private persistStorage(): void {
    navigator.storage.persist().then((granted) => {
      if (granted) {
        console.debug('Opslag is nu persistent.');
      } else {
        console.debug('Opslag is nog steeds niet persistent.');
      }
    });
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (this.db) {
      return Promise.resolve(this.db);
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event: any): void => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('dataStore')) {
          db.createObjectStore('dataStore', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('checklistAnswers')) {
          db.createObjectStore('checklistAnswers', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event: any): void => {
        const db: IDBDatabase = event.target.result;

        // Handle unexpected database closure (can happen after long periods of inactivity)
        db.onclose = (): void => {
          console.warn('IndexedDB connection closed unexpectedly, reconnecting...');
          this.db = null!;
        };

        // Handle version change from another tab
        db.onversionchange = (): void => {
          db.close();
          console.warn('IndexedDB version changed in another tab, please reload the page.');
        };
        this.db = db;
        resolve(this.db);
      };

      request.onerror = (event: any): void => {
        console.error('IndexedDB error:', event.target.error);
        reject(event.target.error);
      };

      request.onblocked = (event: any): void => {
        console.warn('IndexedDB open request blocked:', event);
      };
    });
  }
}